<!-- src/lib/components/BrandAssetsView.svelte -->
<!--
  Brand asset manager view. Three regions:
    1. Left rail: collections list + "+ Collection" button
    2. Main grid: asset thumbnails. Drag-drop files onto it to upload.
    3. Overlay: click any asset to open a zoomed preview.

  Built on top of /api/v1/workspaces/{id}/assets and its collection
  siblings. The backend streams raw bytes at /api/v1/assets/{id}/raw
  which we use directly in <img src> for thumbnails and the zoom overlay.
-->
<script lang="ts">
  import { onMount } from 'svelte';
  import { pmStore } from '../stores/pm.svelte.ts';
  import { api, apiFetch, apiUrl } from '../api.ts';

  interface BrandAsset {
    id:           string;
    workspace_id: string;
    name:         string;
    kind:         'image' | 'svg' | 'logo' | 'font' | 'doc' | string;
    storage_path: string;
    mime:         string;
    size_bytes:   number;
    width:        number | null;
    height:       number | null;
    uploaded_by:  string;
    uploaded_at:  string;
  }

  interface BrandCollection {
    id:           string;
    workspace_id: string;
    name:         string;
    description:  string | null;
    created_at:   string;
  }

  let collections     = $state<BrandCollection[]>([]);
  let assets          = $state<BrandAsset[]>([]);
  let activeCollectionId = $state<string | null>(null); // null = all
  let loading         = $state(false);
  let errorMsg        = $state<string | null>(null);

  let uploading       = $state(false);
  let uploadProgress  = $state<string | null>(null);
  let dragOver        = $state(false);

  let showNewCollection = $state(false);
  let newCollectionName = $state('');

  let zoomAsset = $state<BrandAsset | null>(null);

  function rawUrl(asset: BrandAsset): string {
    return apiUrl(`/api/v1/assets/${asset.id}/raw`);
  }

  async function loadAll() {
    const wsId = pmStore.activeWorkspaceId;
    if (!wsId) return;
    loading = true;
    errorMsg = null;
    try {
      const [col, ast] = await Promise.all([
        api<BrandCollection[]>(`/api/v1/workspaces/${wsId}/collections`),
        activeCollectionId
          ? api<BrandAsset[]>(`/api/v1/workspaces/${wsId}/assets?collection_id=${activeCollectionId}`)
          : api<BrandAsset[]>(`/api/v1/workspaces/${wsId}/assets`),
      ]);
      collections = col;
      assets      = ast;
    } catch (e: unknown) {
      errorMsg = e instanceof Error ? e.message : 'Failed to load assets';
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    void pmStore.activeWorkspaceId;
    void activeCollectionId;
    void loadAll();
  });

  async function createCollection() {
    const wsId = pmStore.activeWorkspaceId;
    if (!wsId || !newCollectionName.trim()) return;
    try {
      const col = await api<BrandCollection>(`/api/v1/workspaces/${wsId}/collections`, {
        method: 'POST',
        body: JSON.stringify({ name: newCollectionName.trim(), description: null }),
      });
      collections = [...collections, col];
      newCollectionName = '';
      showNewCollection = false;
    } catch (e) {
      console.warn('createCollection failed:', e);
    }
  }

  async function deleteCollection(id: string) {
    if (!confirm('Delete this collection? Assets stay; only the collection is removed.')) return;
    await api(`/api/v1/collections/${id}`, { method: 'DELETE' });
    collections = collections.filter(c => c.id !== id);
    if (activeCollectionId === id) activeCollectionId = null;
  }

  async function deleteAsset(asset: BrandAsset) {
    if (!confirm(`Delete "${asset.name}"?`)) return;
    await api(`/api/v1/assets/${asset.id}`, { method: 'DELETE' });
    assets = assets.filter(a => a.id !== asset.id);
    if (zoomAsset?.id === asset.id) zoomAsset = null;
  }

  // ── Upload ─────────────────────────────────────────────────────────
  async function uploadFiles(fileList: FileList | File[]) {
    const wsId = pmStore.activeWorkspaceId;
    if (!wsId) return;
    const files = Array.from(fileList);
    if (files.length === 0) return;

    uploading = true;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      uploadProgress = `${i + 1}/${files.length}: ${file.name}`;
      try {
        const form = new FormData();
        form.append('file', file);
        const res = await apiFetch(`/api/v1/workspaces/${wsId}/assets`, {
          method: 'POST',
          body: form,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
        const asset: BrandAsset = await res.json();
        assets = [asset, ...assets];
        // If a collection is active, add the new asset to it
        if (activeCollectionId) {
          await api(`/api/v1/assets/${asset.id}/collections/${activeCollectionId}`, { method: 'POST' });
        }
      } catch (e) {
        console.warn('upload failed:', file.name, e);
      }
    }
    uploading = false;
    uploadProgress = null;
  }

  function handleDragOver(e: DragEvent) {
    e.preventDefault();
    dragOver = true;
  }
  function handleDragLeave() {
    dragOver = false;
  }
  function handleDrop(e: DragEvent) {
    e.preventDefault();
    dragOver = false;
    if (e.dataTransfer?.files) void uploadFiles(e.dataTransfer.files);
  }

  // Hidden file input for the "Upload" button
  let fileInputEl: HTMLInputElement | null = $state(null);
  function triggerFilePicker() {
    fileInputEl?.click();
  }
  function onFileInputChange(e: Event) {
    const input = e.target as HTMLInputElement;
    if (input.files) void uploadFiles(input.files);
    input.value = '';
  }

  function formatBytes(b: number): string {
    if (b < 1024)        return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  }

  function isPreviewable(a: BrandAsset): boolean {
    return a.mime.startsWith('image/');
  }

  onMount(() => {
    void loadAll();
  });
</script>

<div class="brand-view"
     class:drag-over={dragOver}
     ondragover={handleDragOver}
     ondragleave={handleDragLeave}
     ondrop={handleDrop}
     role="region"
     aria-label="Brand asset manager"
>
  <!-- Left rail: collections -->
  <aside class="brand-sidebar">
    <div class="sidebar-header">
      <span class="header-label">Collections</span>
      <button class="new-btn" onclick={() => { showNewCollection = !showNewCollection; }} title="New collection">+</button>
    </div>

    {#if showNewCollection}
      <form class="new-col-form" onsubmit={(e) => { e.preventDefault(); createCollection(); }}>
        <input
          class="new-col-input"
          bind:value={newCollectionName}
          placeholder="Collection name"
        />
      </form>
    {/if}

    <ul class="col-list">
      <li>
        <button
          class="col-btn"
          class:active={activeCollectionId === null}
          onclick={() => { activeCollectionId = null; }}
        >
          <span class="col-icon">◎</span>
          <span class="col-name">All assets</span>
          <span class="col-count">{assets.length}</span>
        </button>
      </li>
      {#each collections as c (c.id)}
        <li class="col-row">
          <button
            class="col-btn"
            class:active={activeCollectionId === c.id}
            onclick={() => { activeCollectionId = c.id; }}
          >
            <span class="col-icon">◉</span>
            <span class="col-name">{c.name}</span>
          </button>
          <button
            class="col-delete"
            onclick={() => deleteCollection(c.id)}
            title="Delete collection"
          >✕</button>
        </li>
      {/each}
    </ul>
  </aside>

  <!-- Main grid -->
  <section class="brand-main">
    <header class="brand-header">
      <h2 class="brand-title">
        {activeCollectionId
          ? collections.find(c => c.id === activeCollectionId)?.name ?? 'Collection'
          : 'All brand assets'}
      </h2>
      <div class="brand-actions">
        {#if uploading}
          <span class="upload-status">{uploadProgress}</span>
        {/if}
        <button class="upload-btn" onclick={triggerFilePicker} disabled={uploading}>
          {uploading ? 'Uploading…' : '+ Upload'}
        </button>
        <input
          type="file"
          multiple
          bind:this={fileInputEl}
          onchange={onFileInputChange}
          style="display: none;"
        />
      </div>
    </header>

    {#if errorMsg}
      <div class="error-banner">{errorMsg}</div>
    {/if}

    {#if loading && assets.length === 0}
      <div class="empty-state">Loading…</div>
    {:else if assets.length === 0}
      <div class="empty-state">
        <p class="empty-title">No assets yet.</p>
        <p class="empty-hint">Drop files here or click + Upload to add them.</p>
      </div>
    {:else}
      <div class="asset-grid">
        {#each assets as a (a.id)}
          <!-- svelte-ignore a11y_click_events_have_key_events -->
          <!-- svelte-ignore a11y_no_static_element_interactions -->
          <div class="asset-tile" onclick={() => { zoomAsset = a; }}>
            <div class="asset-thumb">
              {#if isPreviewable(a)}
                <img src={rawUrl(a)} alt={a.name} loading="lazy" />
              {:else}
                <div class="asset-placeholder">
                  <span class="placeholder-kind">{a.kind.toUpperCase()}</span>
                </div>
              {/if}
            </div>
            <div class="asset-meta">
              <div class="asset-name" title={a.name}>{a.name}</div>
              <div class="asset-sub">
                {formatBytes(a.size_bytes)}
                {#if a.width && a.height}· {a.width}×{a.height}{/if}
              </div>
            </div>
            <button
              class="asset-delete"
              onclick={(e) => { e.stopPropagation(); deleteAsset(a); }}
              title="Delete"
            >✕</button>
          </div>
        {/each}
      </div>
    {/if}
  </section>

  {#if dragOver}
    <div class="drop-overlay" aria-hidden="true">
      <div class="drop-prompt">Drop files to upload</div>
    </div>
  {/if}
</div>

<!-- Zoom overlay -->
{#if zoomAsset}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div class="zoom-backdrop" onclick={() => { zoomAsset = null; }}>
    <div class="zoom-panel" onclick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Asset preview" tabindex="-1">
      <header class="zoom-header">
        <span class="zoom-name">{zoomAsset.name}</span>
        <span class="zoom-meta">
          {formatBytes(zoomAsset.size_bytes)}
          {#if zoomAsset.width && zoomAsset.height}· {zoomAsset.width}×{zoomAsset.height}{/if}
        </span>
        <button class="zoom-close" onclick={() => { zoomAsset = null; }}>✕</button>
      </header>
      <div class="zoom-body">
        {#if isPreviewable(zoomAsset)}
          <img src={rawUrl(zoomAsset)} alt={zoomAsset.name} />
        {:else}
          <div class="zoom-placeholder">
            <p>{zoomAsset.kind.toUpperCase()}</p>
            <p class="zoom-mime">{zoomAsset.mime}</p>
            <a href={rawUrl(zoomAsset)} download={zoomAsset.name} class="zoom-download">Download</a>
          </div>
        {/if}
      </div>
    </div>
  </div>
{/if}

<style>
  .brand-view {
    display: flex;
    width: 100%;
    height: 100%;
    position: relative;
    color: oklch(0.85 0.04 310);
    overflow: hidden;
  }

  /* ── Sidebar ──────────────────────────────────────────────────── */
  .brand-sidebar {
    width: 220px;
    flex-shrink: 0;
    border-right: 1px solid oklch(0.30 0.05 275 / 0.15);
    background: oklch(0.10 0.02 275 / 0.40);
    display: flex;
    flex-direction: column;
    overflow-y: auto;
  }
  .sidebar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 0.85rem 0.5rem;
  }
  .header-label {
    font-size: 0.62rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: oklch(0.72 0.08 310 / 0.6);
  }
  .new-btn {
    background: transparent;
    border: 1px solid oklch(0.30 0.05 275 / 0.25);
    color: oklch(0.72 0.08 310 / 0.6);
    width: 22px;
    height: 22px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.95rem;
    line-height: 1;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
  }
  .new-btn:hover {
    background: oklch(0.72 0.28 310 / 0.14);
    border-color: oklch(0.72 0.28 310 / 0.35);
    color: oklch(0.92 0.15 310);
  }
  .new-col-form {
    padding: 0 0.85rem 0.6rem;
  }
  .new-col-input {
    width: 100%;
    box-sizing: border-box;
    background: oklch(0.14 0.02 275 / 0.70);
    border: 1px solid oklch(0.72 0.28 310 / 0.32);
    border-radius: 4px;
    padding: 0.35rem 0.5rem;
    color: oklch(0.92 0.03 310);
    font-size: 0.78rem;
    outline: none;
  }
  .col-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .col-row {
    display: flex;
    align-items: center;
  }
  .col-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
    background: transparent;
    border: none;
    color: oklch(0.70 0.04 310 / 0.75);
    padding: 0.4rem 0.85rem;
    font-size: 0.8rem;
    text-align: left;
    cursor: pointer;
    transition: background-color 100ms ease, color 100ms ease;
  }
  .col-btn:hover {
    background: oklch(0.72 0.28 310 / 0.06);
    color: oklch(0.92 0.06 310);
  }
  .col-btn.active {
    background: oklch(0.72 0.28 310 / 0.12);
    color: oklch(0.96 0.12 310);
  }
  .col-icon {
    color: oklch(0.55 0.05 310 / 0.5);
    font-size: 0.75rem;
    flex-shrink: 0;
  }
  .col-btn.active .col-icon {
    color: oklch(0.78 0.26 310);
  }
  .col-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .col-count {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.65rem;
    color: oklch(0.55 0.04 310 / 0.5);
  }
  .col-delete {
    background: transparent;
    border: none;
    color: oklch(0.45 0.04 310 / 0.4);
    padding: 0.25rem 0.6rem 0.25rem 0.25rem;
    cursor: pointer;
    font-size: 0.72rem;
    transition: color 100ms ease;
  }
  .col-delete:hover {
    color: oklch(0.72 0.22 25 / 0.8);
  }

  /* ── Main ─────────────────────────────────────────────────────── */
  .brand-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .brand-header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.5rem 0.75rem;
    border-bottom: 1px solid oklch(0.30 0.05 275 / 0.15);
  }
  .brand-title {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: oklch(0.94 0.06 310);
  }
  .brand-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  .upload-status {
    font-size: 0.72rem;
    color: oklch(0.72 0.18 85 / 0.8);
    font-family: 'JetBrains Mono', monospace;
  }
  .upload-btn {
    background: oklch(0.72 0.28 310 / 0.14);
    border: 1px solid oklch(0.72 0.28 310 / 0.35);
    color: oklch(0.88 0.18 310);
    padding: 0.4rem 0.9rem;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.78rem;
    font-weight: 500;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
  }
  .upload-btn:hover:not(:disabled) {
    background: oklch(0.72 0.28 310 / 0.24);
    border-color: oklch(0.80 0.26 310 / 0.55);
    color: oklch(0.96 0.15 310);
  }
  .upload-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .error-banner {
    background: oklch(0.22 0.14 25 / 0.30);
    border-bottom: 1px solid oklch(0.62 0.26 25 / 0.4);
    color: oklch(0.85 0.15 25);
    padding: 0.6rem 1.5rem;
    font-size: 0.78rem;
  }

  /* ── Grid ─────────────────────────────────────────────────────── */
  .asset-grid {
    flex: 1;
    overflow-y: auto;
    padding: 1.25rem 1.5rem;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 1rem;
  }
  .asset-tile {
    position: relative;
    background: oklch(0.12 0.02 275 / 0.60);
    border: 1px solid oklch(0.30 0.05 275 / 0.20);
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    transition: border-color 120ms ease, background-color 120ms ease;
    display: flex;
    flex-direction: column;
    contain: layout style paint;
  }
  .asset-tile:hover {
    border-color: oklch(0.72 0.28 310 / 0.40);
    background: oklch(0.14 0.03 275 / 0.70);
  }
  .asset-thumb {
    aspect-ratio: 1;
    background: oklch(0.08 0.02 275);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }
  .asset-thumb img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
  .asset-placeholder {
    color: oklch(0.55 0.05 310 / 0.6);
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 0.85rem;
  }
  .placeholder-kind {
    letter-spacing: 0.08em;
  }
  .asset-meta {
    padding: 0.55rem 0.7rem;
    border-top: 1px solid oklch(0.30 0.05 275 / 0.15);
  }
  .asset-name {
    font-size: 0.78rem;
    font-weight: 500;
    color: oklch(0.92 0.04 310);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .asset-sub {
    font-size: 0.65rem;
    color: oklch(0.55 0.04 310 / 0.6);
    margin-top: 0.1rem;
  }
  .asset-delete {
    position: absolute;
    top: 0.4rem;
    right: 0.4rem;
    background: oklch(0.08 0.02 275 / 0.80);
    border: 1px solid oklch(0.30 0.05 275 / 0.3);
    color: oklch(0.72 0.04 310 / 0.7);
    width: 22px;
    height: 22px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.72rem;
    opacity: 0;
    transition: opacity 100ms ease, background-color 100ms ease, color 100ms ease;
  }
  .asset-tile:hover .asset-delete {
    opacity: 1;
  }
  .asset-delete:hover {
    background: oklch(0.20 0.15 25 / 0.8);
    border-color: oklch(0.55 0.22 25 / 0.6);
    color: oklch(0.90 0.10 25);
  }

  .empty-state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: oklch(0.55 0.04 310 / 0.55);
  }
  .empty-title {
    margin: 0 0 0.4rem 0;
    font-size: 0.95rem;
    color: oklch(0.72 0.08 310 / 0.7);
  }
  .empty-hint {
    font-size: 0.8rem;
    color: oklch(0.50 0.03 310 / 0.5);
    margin: 0;
  }

  /* ── Drop overlay ─────────────────────────────────────────────── */
  .drop-overlay {
    position: absolute;
    inset: 0;
    background: oklch(0.72 0.28 310 / 0.12);
    border: 2px dashed oklch(0.78 0.26 310 / 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    z-index: 5;
  }
  .drop-prompt {
    font-size: 1.1rem;
    font-weight: 500;
    color: oklch(0.96 0.15 310);
    background: oklch(0.08 0.02 275 / 0.8);
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
  }

  /* ── Zoom overlay ─────────────────────────────────────────────── */
  .zoom-backdrop {
    position: fixed;
    inset: 0;
    background: oklch(0.04 0.01 275 / 0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
  }
  .zoom-panel {
    max-width: 90vw;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    background: oklch(0.11 0.02 275);
    border: 1px solid oklch(0.30 0.05 275 / 0.35);
    border-radius: 10px;
    overflow: hidden;
  }
  .zoom-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid oklch(0.30 0.05 275 / 0.15);
  }
  .zoom-name {
    font-weight: 600;
    color: oklch(0.94 0.06 310);
    flex: 1;
  }
  .zoom-meta {
    font-size: 0.75rem;
    color: oklch(0.55 0.04 310 / 0.6);
    font-family: 'JetBrains Mono', monospace;
  }
  .zoom-close {
    background: transparent;
    border: 1px solid oklch(0.30 0.05 275 / 0.3);
    color: oklch(0.72 0.04 310 / 0.7);
    width: 28px;
    height: 28px;
    border-radius: 5px;
    cursor: pointer;
    font-size: 0.8rem;
    transition: background-color 100ms ease, color 100ms ease;
  }
  .zoom-close:hover {
    background: oklch(0.18 0.02 275);
    color: oklch(0.92 0.04 310);
  }
  .zoom-body {
    flex: 1;
    overflow: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    background: oklch(0.06 0.01 275);
  }
  .zoom-body img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
  .zoom-placeholder {
    text-align: center;
    color: oklch(0.72 0.04 310 / 0.7);
  }
  .zoom-mime {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.78rem;
    color: oklch(0.55 0.04 310 / 0.6);
    margin: 0.35rem 0 1rem;
  }
  .zoom-download {
    display: inline-block;
    background: oklch(0.72 0.28 310 / 0.14);
    border: 1px solid oklch(0.72 0.28 310 / 0.35);
    color: oklch(0.88 0.18 310);
    padding: 0.5rem 1rem;
    border-radius: 6px;
    text-decoration: none;
    font-size: 0.82rem;
  }
  .zoom-download:hover {
    background: oklch(0.72 0.28 310 / 0.24);
    color: oklch(0.96 0.15 310);
  }
</style>
